import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { Dropdown } from 'react-bootstrap';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { Modal } from 'bootstrap';
import './modal.css'; // Import custom modal CSS
import './stats-cards.css'; // Import stats card styles
import './collections-actions.css'; // Dropdown item styles
import { getDueCollectionsByDateRange, getDueCollectionStats, useRecordPayment, getLedgerAccountById, useGetEarlyRepaymentQuote, useRecordEarlyRepayment } from '../../../services/loanService';
import { GetDueCollectionsByDateRangeStatus } from '../../../services/schemas/loanServiceSchemas/getDueCollectionsByDateRangeStatus';
import type { DueCollection as ApiDueCollection } from '../../../services/schemas/loanServiceSchemas/dueCollection';
import type { DueCollectionStats } from '../../../services/schemas/loanServiceSchemas/dueCollectionStats';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { LedgerAccount } from '../../../services/schemas/loanServiceSchemas/ledgerAccount';
import type { EarlyRepaymentQuote } from '../../../services/schemas/loanServiceSchemas/earlyRepaymentQuote';
import { useAddCollectionNote } from '../../../services/hooks/collectionNoteHooks';
import type { CollectionNote } from '../../../services/schemas/loanServiceSchemas/collectionNote';
import { getCollectionNotes } from '../../../services/collectionNoteService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { toLocalDateString } from '../../../utils/localDate';
import SendReminderModal, { ReminderTarget, describeReminder } from './SendReminderModal';

type CollectionNoteContext = 'GENERAL' | 'ARRANGEMENTS' | 'COLLECTION' | 'APPLICATION_NOTES';

// Define types for due collections
interface DueCollection {
  id: string;
  accountNumber: string;
  customerName: string;
  loanAmount: number;
  dueAmount: number;
  dueDate: string;
  status: 'Due Today' | 'Due Soon' | 'Partially Paid' | 'Overdue';
  contactNumber: string;
  email: string;
  daysToDue: number;
  loanAccountId: string;
  paymentId: string;
  totalOutstandingAmount?: number;
}

// Define payment form schema
const paymentFormSchema = z.object({
  paymentAmount: z.string()
      .min(1, 'Payment amount is required')
      .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
        message: 'Payment amount must be greater than 0'
      }),
  valueDate: z.string()
      .min(1, 'Payment date is required')
      .refine((val) => !isNaN(Date.parse(val)), {
        message: 'Please select a valid date'
      }),
  externalRef: z.string().optional(),
  remarks: z.string().optional()
});

type PaymentFormData = z.infer<typeof paymentFormSchema>;

// Add early repayment form schema
const earlyRepaymentFormSchema = z.object({
  valueDate: z.string()
      .min(1, 'Payment date is required')
      .refine((val) => !isNaN(Date.parse(val)), {
        message: 'Please select a valid date'
      }),
  externalRef: z.string().optional(),
  remarks: z.string().optional()
});

type EarlyRepaymentFormData = z.infer<typeof earlyRepaymentFormSchema>;


/** Whole days from today to a yyyy-mm-dd date: negative once it has passed. */
const daysFromToday = (date: string): number => {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  const today = new Date();
  const due = new Date(y, m - 1, d);
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((due.getTime() - startOfToday.getTime()) / 86_400_000);
};

const DueCollections = () => {
  const [size, setSize] = useState(10);
  const ALL_DUE_COLLECTIONS_PAGE_SIZE = 10000;
  const [isLoading, setIsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<GetDueCollectionsByDateRangeStatus | undefined>(undefined);
  const [customerName, setCustomerName] = useState('');
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    // Default to full current month to avoid overly narrow initial results.
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
      startDate: toLocalDateString(startDate),
      endDate: toLocalDateString(endDate)
    };
  });
  const [selectedCollection, setSelectedCollection] = useState<DueCollection | null>(null);
  // Send Reminder: the loan the reminder dialog is open for
  const [reminderTarget, setReminderTarget] = useState<ReminderTarget | null>(null);
  const [stats, setStats] = useState<DueCollectionStats>({
    total: 0,
    dueToday: 0,
    dueSoon: 0,
    partiallyPaid: 0
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'info';
    message: string;
  }>({
    show: false,
    type: 'info',
    message: ''
  });
  const [showEarlyRepaymentModal, setShowEarlyRepaymentModal] = useState(false);
  const [loanAccount, setLoanAccount] = useState<LedgerAccount | null>(null);
  const [earlyRepaymentCalculation, setEarlyRepaymentCalculation] = useState<EarlyRepaymentQuote | null>(null);
  const [isCalculatingEarlyRepayment, setIsCalculatingEarlyRepayment] = useState(false);
  const [isProcessingEarlyRepayment, setIsProcessingEarlyRepayment] = useState(false);
  const navigate = useNavigate();

  // Collection Notes state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteContext, setNoteContext] = useState<CollectionNoteContext>('GENERAL');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [lastNotes, setLastNotes] = useState<Record<string, string>>({});
  const [showViewNotesModal, setShowViewNotesModal] = useState(false);
  const [viewNotesCollection, setViewNotesCollection] = useState<DueCollection | null>(null);
  const [allNotes, setAllNotes] = useState<CollectionNote[]>([]);
  const [isLoadingAllNotes, setIsLoadingAllNotes] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const addNoteMutation = useAddCollectionNote();

  // Payment mutation
  const recordPaymentMutation = useRecordPayment({
    mutation: {
      onSuccess: () => {
        toast.success('Payment processed successfully');
        fetchDueCollections();
        fetchStats();

        // Close modal only after successful response
        const modalElement = document.getElementById('DueCollectionPayment');
        if (modalElement) {
          const modal = Modal.getInstance(modalElement);
          if (modal) modal.hide();
        }
        setShowPaymentModal(false);
        reset();
        setIsProcessingPayment(false);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to process payment');
        setIsProcessingPayment(false);
        // Don't close modal on error so user can retry
      }
    }
  });

  // Early repayment quote mutation
  const getEarlyRepaymentQuoteMutation = useGetEarlyRepaymentQuote({
    mutation: {
      onSuccess: (data) => {
        setEarlyRepaymentCalculation(data);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to calculate early repayment');
        setIsCalculatingEarlyRepayment(false);
      }
    }
  });

  // Early repayment mutation
  const recordEarlyRepaymentMutation = useRecordEarlyRepayment({
    mutation: {
      onSuccess: () => {
        toast.success('Early repayment processed successfully');
        fetchDueCollections();
        fetchStats();

        // Close modal only after successful response
        const modalElement = document.getElementById('DueCollectionEarlyRepayment');
        if (modalElement) {
          const modal = Modal.getInstance(modalElement);
          if (modal) modal.hide();
        }
        setShowEarlyRepaymentModal(false);
        resetEarlyRepayment();
        setIsProcessingEarlyRepayment(false);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to process early repayment');
        setIsProcessingEarlyRepayment(false);
        // Don't close modal on error so user can retry
      }
    }
  });

  // State for collections data
  const [dueCollections, setDueCollections] = useState<{
    content: DueCollection[];
    totalElements: number;
  }>({
    content: [],
    totalElements: 0
  });

  // Initialize form
  const methods = useForm<PaymentFormData>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      paymentAmount: undefined,
      valueDate: toLocalDateString(),
      externalRef: '',
      remarks: ''
    }
  });

  const { register, handleSubmit, formState: { errors }, reset, setValue } = methods;

  // Initialize early repayment form
  const earlyRepaymentMethods = useForm<EarlyRepaymentFormData>({
    resolver: zodResolver(earlyRepaymentFormSchema),
    defaultValues: {
      valueDate: toLocalDateString(),
      externalRef: '',
      remarks: ''
    }
  });

  const { register: registerEarlyRepayment, handleSubmit: handleSubmitEarlyRepayment, formState: { errors: earlyRepaymentErrors }, reset: resetEarlyRepayment } = earlyRepaymentMethods;

  // Fetch due collections
  const fetchDueCollections = async () => {
    setIsLoading(true);
    try {
      const response = await getDueCollectionsByDateRange({
        page: 0,
        size: ALL_DUE_COLLECTIONS_PAGE_SIZE,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...(filterStatus && { status: filterStatus }),
        ...(customerName && { customerName: customerName })
      });

      // Map API response to component interface
      const mappedContent = response.content?.map((item: ApiDueCollection) => ({
        id: item.id || '',
        accountNumber: item.accountNumber || '',
        customerName: item.customerName || '',
        loanAmount: item.loanAmount || 0,
        dueAmount: item.dueAmount || 0,
        dueDate: item.dueDate || '',
        status: item.status as DueCollection['status'],
        contactNumber: item.contactNumber || '',
        email: item.email || '',
        // From the due date itself: the API floors this at 0, which showed a missed installment as "Due Today"
        daysToDue: item.dueDate ? daysFromToday(item.dueDate) : (item.daysToDue || 0),
        loanAccountId: item.loanAccountId || '',
        paymentId: item.paymentId || '',
        totalOutstandingAmount: item.totalOutstandingAmount || 0
      })) || [];

      setDueCollections({
        content: mappedContent,
        totalElements: mappedContent.length
      });

      // Fetch last notes for the loaded collections
      fetchLastNotes(mappedContent);
    } catch (error) {
      console.error('Error fetching due collections:', error);
      toast.error('Failed to fetch due collections');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await getDueCollectionStats();
      setStats(response || { total: 0, dueToday: 0, dueSoon: 0, partiallyPaid: 0 });
    } catch (error) {
      console.error('Error fetching due collection stats:', error);
      toast.error('Failed to fetch collection statistics');
    }
  };

  // Fetch data when filters or pagination changes
  useEffect(() => {
    fetchDueCollections();
    fetchStats();
  }, [filterStatus, customerName, dateRange]);

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Overdue':
      case 'Due Today':
        return 'danger';
      case 'Due Soon':
        return 'warning';
      case 'Partially Paid':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Update to show reminder modal instead of navigating to payment page
  const handleShowPaymentModal = (collection: DueCollection) => {
    setReminderTarget({
      accountId: collection.loanAccountId,
      accountNumber: collection.accountNumber,
      customerName: collection.customerName,
    });
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number) => {
    setSize(newPerPage);
  };

  // Handle filter change
  const handleFilterChange = (status: GetDueCollectionsByDateRangeStatus | undefined) => {
    setFilterStatus(status);
  };

  // Handle customer name search
  const handleCustomerNameSearch = (name: string) => {
    setCustomerName(name);
  };

  // Handle date range change
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  };

  // Handle opening early repayment modal
  const handleOpenEarlyRepaymentModal = async (collection: DueCollection) => {
    console.log('Opening early repayment modal for collection:', collection);
    setSelectedCollection(collection);
    setShowEarlyRepaymentModal(true);
    setEarlyRepaymentCalculation(null); // Reset calculation

    try {
      // Get loan account details first
      const loanAccountResponse = await getLedgerAccountById(collection.loanAccountId);
      console.log('Loan account loaded:', loanAccountResponse);
      setLoanAccount(loanAccountResponse);

      // Only show the modal after loan account is loaded
      const modalElement = document.getElementById('DueCollectionEarlyRepayment');
      if (modalElement) {
        const modal = new Modal(modalElement);
        modal.show();
      }
    } catch (error) {
      console.error('Error preparing early repayment:', error);
      toast.error('Failed to prepare early repayment');
      // Close modal if there's an error
      const modalElement = document.getElementById('DueCollectionEarlyRepayment');
      if (modalElement) {
        const modal = Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
      setShowEarlyRepaymentModal(false);
    }
  };

  // Calculate early repayment amount
  const calculateEarlyRepaymentAmount = async () => {
    if (!selectedCollection || !loanAccount) return;

    setIsCalculatingEarlyRepayment(true);
    try {
      getEarlyRepaymentQuoteMutation.mutate({
        data: {
          accountId: selectedCollection.loanAccountId,
          quoteDate: toLocalDateString()
        }
      });
    } catch (error) {
      console.error('Error calculating early repayment:', error);
      setIsCalculatingEarlyRepayment(false);
    }
  };

  // Update loading state based on mutation status
  useEffect(() => {
    setIsProcessingPayment(recordPaymentMutation.isPending);
  }, [recordPaymentMutation.isPending]);

  useEffect(() => {
    setIsProcessingEarlyRepayment(recordEarlyRepaymentMutation.isPending);
  }, [recordEarlyRepaymentMutation.isPending]);

  // Update early repayment calculation when mutation succeeds
  useEffect(() => {
    if (getEarlyRepaymentQuoteMutation.isSuccess && getEarlyRepaymentQuoteMutation.data) {
      setEarlyRepaymentCalculation(getEarlyRepaymentQuoteMutation.data);
      setIsCalculatingEarlyRepayment(false);

      // Set default form values
      resetEarlyRepayment({
        valueDate: toLocalDateString(),
        externalRef: '',
        remarks: ''
      });
    }
  }, [getEarlyRepaymentQuoteMutation.isSuccess, getEarlyRepaymentQuoteMutation.data, resetEarlyRepayment]);

  // Handle early repayment submission
  const handleProcessEarlyRepayment = async (data: EarlyRepaymentFormData) => {
    if (!selectedCollection || !loanAccount) {
      toast.error('No collection or loan account selected');
      return;
    }

    try {
      setIsProcessingEarlyRepayment(true);

      const earlyRepaymentRequest = {
        accountId: selectedCollection.loanAccountId,
        valueDate: data.valueDate,
        idempotencyKey: `early-repayment-${selectedCollection.loanAccountId}-${Date.now()}`,
        externalRef: data.externalRef || undefined,
        remarks: data.remarks || undefined
      };

      // The mutation will handle closing the modal on success
      recordEarlyRepaymentMutation.mutate({ data: earlyRepaymentRequest });
    } catch (error) {
      console.error('Error processing early repayment:', error);
      toast.error('Failed to process early repayment');
      setIsProcessingEarlyRepayment(false);
    }
  };

  // Update handleOpenPaymentModal to fetch loan account and properly set form values
  const handleOpenPaymentModal = async (collection: DueCollection) => {
    console.log('Opening payment modal for collection:', collection);
    setSelectedCollection(collection);

    try {
      // Fetch loan account details for outstanding balance
      const loanAccountResponse = await getLedgerAccountById(collection.loanAccountId);
      setLoanAccount(loanAccountResponse);

      // Reset form and set new values
      reset({
        // Default to what is due on this installment; settling the whole loan is the early repayment action
        paymentAmount: (collection.dueAmount || loanAccountResponse?.totalOutstanding || 0).toString(),
        valueDate: toLocalDateString(),
        externalRef: '',
        remarks: ''
      });

      setShowPaymentModal(true);

      const modalElement = document.getElementById('DueCollectionPayment');
      if (modalElement) {
        const modal = new Modal(modalElement);
        modal.show();
      }
    } catch (error) {
      console.error('Error fetching loan account:', error);
      toast.error('Failed to load account details');
    }
  };

  // Update handleProcessPayment to handle the validated data
  const handleProcessPayment = async (data: PaymentFormData) => {
    if (!selectedCollection) {
      toast.error('No collection selected');
      return;
    }

    try {
      setIsProcessingPayment(true);

      const paymentRequest = {
        accountId: selectedCollection.loanAccountId,
        amount: Number(data.paymentAmount),
        valueDate: data.valueDate,
        idempotencyKey: `payment-${selectedCollection.loanAccountId}-${Date.now()}`,
        externalRef: data.externalRef || undefined,
        remarks: data.remarks || undefined
      };

      // The mutation will handle closing the modal on success.
      recordPaymentMutation.mutate({ data: paymentRequest });
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Failed to process payment');
      setIsProcessingPayment(false);
    }
  };

  // Add this helper function near other utility functions
  const getDaysBetweenDates = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
  };

  // Handle opening the Add Note modal
  const handleOpenNoteModal = (collection: DueCollection) => {
    setSelectedCollection(collection);
    setNoteText('');
    setNoteContext('GENERAL');
    setShowNoteModal(true);

    const modalElement = document.getElementById('DueCollectionNote');
    if (modalElement) {
      const modal = new Modal(modalElement);
      modal.show();
    }
  };

  // Handle submitting a collection note
  const handleSubmitNote = async () => {
    if (!selectedCollection || !noteText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    setIsSubmittingNote(true);
    try {
      await addNoteMutation.mutateAsync({
        accountId: selectedCollection.loanAccountId,
        note: noteText.trim(),
        context: noteContext,
        createdBy: user?.name || user?.username || undefined,
      });
      toast.success('Note added successfully');
      setNoteText('');

      // Update the last note in the table
      setLastNotes(prev => ({
        ...prev,
        [selectedCollection.loanAccountId]: noteText.trim()
      }));

      // Close modal
      const modalElement = document.getElementById('DueCollectionNote');
      if (modalElement) {
        const modal = Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }
      setShowNoteModal(false);
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    } finally {
      setIsSubmittingNote(false);
    }
  };

  // Fetch last notes for all accounts in the table
  const fetchLastNotes = async (collections: DueCollection[]) => {
    const accountIds = collections.map(c => c.loanAccountId).filter(Boolean);
    const uniqueAccountIds = [...new Set(accountIds)];

    const notesMap: Record<string, string> = {};
    await Promise.allSettled(
      uniqueAccountIds.map(async (accountId) => {
        try {
          const response = await getCollectionNotes(accountId, { page: 0, size: 1 });
          if (response.content && response.content.length > 0) {
            notesMap[accountId] = response.content[0].note || '';
          }
        } catch {
          // Silently fail for individual note fetches
        }
      })
    );
    setLastNotes(prev => ({ ...prev, ...notesMap }));
  };

  // Handle viewing all notes for an account
  const handleViewAllNotes = async (row: DueCollection) => {
    setViewNotesCollection(row);
    setShowViewNotesModal(true);
    setIsLoadingAllNotes(true);
    setAllNotes([]);

    try {
      const response = await getCollectionNotes(row.loanAccountId, { page: 0, size: 50 });
      setAllNotes(response.content || []);
    } catch {
      toast.error('Failed to load notes');
    } finally {
      setIsLoadingAllNotes(false);
    }
  };

  // Define table columns
  const columns = [
    {
      name: 'Account Number ',
      selector: (row: DueCollection) => row.accountNumber,
      sortable: true,
    },
    {
      name: 'Customer',
      selector: (row: DueCollection) => row.customerName,
      sortable: true,
    },
    {
      name: 'Total Outstanding',
      selector: (row: DueCollection) => row.totalOutstandingAmount || 0,
      sortable: true,
      cell: (row: DueCollection) => (
        <div className="text-end fw-bold text-danger">
          {formatCurrency(row.totalOutstandingAmount || 0)}
        </div>
      ),
    },
    {
      name: 'Due Amount',
      selector: (row: DueCollection) => row.dueAmount,
      sortable: true,
      format: (row: DueCollection) => formatCurrency(row.dueAmount),
    },
    {
      name: 'Due Date',
      selector: (row: DueCollection) => row.dueDate,
      sortable: true,
      format: (row: DueCollection) => formatDate(row.dueDate),
    },
    {
      name: 'Days to Due',
      selector: (row: DueCollection) => row.daysToDue,
      sortable: true,
      cell: (row: DueCollection) => (
        <span className={row.daysToDue <= 0 ? 'text-danger fw-bold' : 'text-warning'}>
          {row.daysToDue < 0
            ? `${-row.daysToDue} day${row.daysToDue === -1 ? '' : 's'} late`
            : row.daysToDue === 0 ? 'Due Today' : `${row.daysToDue} days`}
        </span>
      ),
    },
    {
      name: 'Status',
      selector: (row: DueCollection) => row.status,
      sortable: true,
      cell: (row: DueCollection) => (
        <span className={`badge bg-${getStatusColor(row.status)}`}>
          {row.status}
        </span>
      ),
    },
    {
      name: 'Last Note',
      cell: (row: DueCollection) => {
        const note = lastNotes[row.loanAccountId];
        return note ? (
          <div className="py-1" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={note}>
            <i className="fa fa-sticky-note text-success me-1"></i>
            <span className="small text-muted">{note}</span>
          </div>
        ) : (
          <span className="text-muted small fst-italic">No notes</span>
        );
      },
      width: '200px',
    },
    {
      name: 'Actions',
      cell: (row: DueCollection) => (
        <Dropdown align="end">
          <Dropdown.Toggle variant="light" size="sm" id={`actions-${row.id}`} className="rounded-pill d-flex align-items-center no-caret">
            <span>Actions</span>
            <i className="fa fa-caret-down ms-2"></i>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item className="action-pay" onClick={() => handleOpenPaymentModal(row)}>
              <i className="fa fa-credit-card me-2"></i>
              Process Payment
            </Dropdown.Item>
            <Dropdown.Item className="action-early" onClick={() => handleOpenEarlyRepaymentModal(row)}>
              <i className="fa fa-handshake-o me-2"></i>
              Early Repayment
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item className="action-note" onClick={() => handleOpenNoteModal(row)}>
              <i className="fa fa-sticky-note me-2 text-success"></i>
              Add Remarks
            </Dropdown.Item>
            <Dropdown.Item className="action-view-notes" onClick={() => handleViewAllNotes(row)}>
              <i className="fa fa-list-alt me-2 text-info"></i>
              View All Remarks
            </Dropdown.Item>
            <Dropdown.Item className="action-reminder" onClick={() => handleShowPaymentModal(row)}>
              <i className="fa fa-bell me-2"></i>
              Send Reminder
            </Dropdown.Item>
            <Dropdown.Item className="action-view" onClick={() => {
              const accountId = row.loanAccountId || row.id;
              if (!accountId) {
                toast.error('Cannot open loan account: missing account ID');
                return;
              }
              navigate(`/loan-accounts/${accountId}`);
            }}>
              <i className="fa fa-eye me-2"></i>
              View Loan Account
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ),
    },
  ];

  // Initialize modals
  useEffect(() => {
    const earlyRepaymentModal = document.getElementById('DueCollectionEarlyRepayment');
    if (earlyRepaymentModal) {
      const modal = new Modal(earlyRepaymentModal);
      earlyRepaymentModal.addEventListener('hidden.bs.modal', () => {
        setShowEarlyRepaymentModal(false);
        setSelectedCollection(null);
        setLoanAccount(null);
        setEarlyRepaymentCalculation(null);
        earlyRepaymentMethods.reset();
      });
    }
  }, [earlyRepaymentMethods]);

  // Add debug logging to the modal render
  useEffect(() => {
    console.log('Modal state:', {
      showEarlyRepaymentModal,
      earlyRepaymentCalculation,
      loanAccount,
      selectedCollection
    });
  }, [showEarlyRepaymentModal, earlyRepaymentCalculation, loanAccount, selectedCollection]);

  return (
      <div>
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
          <div className="container-fluid">
            <div className="row g-3 mb-3 align-items-center">
              <div className="col">
                <ol className="breadcrumb bg-transparent mb-0">
                  <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                  <li className="breadcrumb-item"><Link className="text-secondary" to="/collections">Collections</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Due Collections </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
          <div className="container-fluid">
            {/* Alert Component */}
            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show mb-4`} role="alert">
                  {alert.message}
                  <button
                      type="button"
                      className="btn-close"
                      onClick={() => setAlert({ ...alert, show: false })}
                      aria-label="Close"
                  ></button>
                </div>
            )}

            {/* Stats Cards - Compact Version */}
            <div className="row g-3 mb-4">
              <div className="col-6 col-md-3">
                <div className="card shadow-sm h-100">
                  <div className="card-body d-flex align-items-center px-3 py-2">
                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                      <i className="fa fa-list-alt text-primary" style={{ display: 'block', fontSize: '1.5rem' }}></i>
                    </div>
                    <div>
                      <h6 className="card-title mb-0 small text-muted">Total Due</h6>
                      <h3 className="mb-0 fs-4 fw-bold">{stats.total || 0}</h3>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card shadow-sm h-100">
                  <div className="card-body d-flex align-items-center px-3 py-2">
                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(220,53,69,0.2)' }}>
                      <i className="fa fa-calendar-check-o text-danger" style={{ display: 'block', fontSize: '1.5rem' }}></i>
                    </div>
                    <div>
                      <h6 className="card-title mb-0 small text-muted">Due Today</h6>
                      <h3 className="mb-0 fs-4 fw-bold">{stats.dueToday || 0}</h3>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card shadow-sm h-100">
                  <div className="card-body d-flex align-items-center px-3 py-2">
                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(255,193,7,0.2)' }}>
                      <i className="fa fa-calendar text-warning" style={{ display: 'block', fontSize: '1.5rem' }}></i>
                    </div>
                    <div>
                      <h6 className="card-title mb-0 small text-muted">Due Soon</h6>
                      <h3 className="mb-0 fs-4 fw-bold">{stats.dueSoon || 0}</h3>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card shadow-sm h-100">
                  <div className="card-body d-flex align-items-center px-3 py-2">
                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(23,162,184,0.2)' }}>
                      <i className="fa fa-money text-info" style={{ display: 'block', fontSize: '1.5rem' }}></i>
                    </div>
                    <div>
                      <h6 className="card-title mb-0 small text-muted">Partially Paid</h6>
                      <h3 className="mb-0 fs-4 fw-bold">{stats.partiallyPaid || 0}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Section */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Filter by Status</label>
                        <select
                          className="form-select"
                          value={filterStatus || ''}
                          onChange={(e) => handleFilterChange(e.target.value === '' ? undefined : e.target.value as GetDueCollectionsByDateRangeStatus)}
                        >
                          <option value="">All Collections</option>
                          <option value={GetDueCollectionsByDateRangeStatus.Due_Today}>Due Today</option>
                          <option value={GetDueCollectionsByDateRangeStatus.Due_Soon}>Due Soon</option>
                          <option value={GetDueCollectionsByDateRangeStatus.Partially_Paid}>Partially Paid</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Customer Name</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name"
                          value={customerName}
                          onChange={(e) => handleCustomerNameSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <label className="form-label">Date Range</label>
                        <div className="d-flex align-items-center gap-2">
                          <div className="input-group">
                            <span className="input-group-text bg-light">
                              <i className="fa fa-calendar text-primary"></i>
                            </span>
                            <input
                              type="date"
                              className="form-control"
                              value={dateRange.startDate}
                              onChange={(e) => handleDateRangeChange(e.target.value, dateRange.endDate)}
                            />
                            <span className="input-group-text bg-light border-start-0 border-end-0">
                              <i className="fa fa-arrow-right text-muted"></i>
                            </span>
                            <input
                              type="date"
                              className="form-control"
                              value={dateRange.endDate}
                              onChange={(e) => handleDateRangeChange(dateRange.startDate, e.target.value)}
                            />
                            <span className="input-group-text bg-light">
                              <i className="fa fa-calendar text-primary"></i>
                            </span>
                          </div>
                          <div className="d-flex align-items-center bg-primary-subtle rounded px-3 py-2" style={{ minWidth: '100px', justifyContent: 'center' }}>
                            <i className="fa fa-clock text-primary me-2"></i>
                            <span className="text-primary fw-medium">
                              {getDaysBetweenDates(dateRange.startDate, dateRange.endDate)}
                            </span>
                            <span className="text-primary ms-1">days</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Due Collections Table */}
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="card-title mb-0">Due Collections</h6>
                      <small className="text-muted">{dueCollections.content.length} records loaded</small>
                    </div>
                    <div className="dropdown">
                      <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                        <i className="fa fa-download me-1"></i>Export
                      </button>
                      <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                        <li><a className="dropdown-item" href="#">Export as CSV</a></li>
                        <li><a className="dropdown-item" href="#">Export as Excel</a></li>
                        <li><a className="dropdown-item" href="#">Export as PDF</a></li>
                      </ul>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2">Loading due collections...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                          <DataTable
                              columns={columns}
                              data={dueCollections.content}
                              pagination
                              onChangeRowsPerPage={handlePerRowsChange}
                              paginationPerPage={size}
                              paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
                              paginationComponentOptions={{
                                rowsPerPageText: 'Rows per page:',
                                rangeSeparatorText: 'of',
                              }}
                              highlightOnHover
                              striped
                              responsive
                              progressPending={isLoading}
                          />
                        </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        <div className="modal fade modal-slide-in-left" id="DueCollectionPayment" tabIndex={-1} aria-labelledby="DueCollectionPaymentLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-vertical modal-dialog-scrollable" style={{ maxWidth: '900px', width: '90%' }}>
            <div className="modal-content rounded-0 border-0 shadow-lg">
              {/* Header */}
              <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="w-100">
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                        <i className="fa fa-credit-card fa-2x text-primary" style={{ display: 'block' }}></i>
                      </div>
                      <div>
                        <h4 className="modal-title mb-0 text-dark fw-bold" id="DueCollectionPaymentLabel">
                          Process Payment
                        </h4>
                        <p className="text-muted mb-0 mt-1 small">Record payment transaction for this account</p>
                      </div>
                    </div>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>

                  {/* Quick Info Cards */}
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                            <i className="fa fa-user text-primary" style={{ display: 'block' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="text-muted small">Customer</div>
                            <div className="text-dark fw-semibold small">{selectedCollection?.customerName || loanAccount?.customerNumber || 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(255,193,7,0.2)' }}>
                            <i className="fa fa-money text-warning" style={{ display: 'block' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="text-muted small">Outstanding</div>
                            <div className="text-danger fw-bold">{formatCurrency(loanAccount?.totalOutstanding || selectedCollection?.dueAmount || 0)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="modal-body custom_scroll p-4" style={{ backgroundColor: '#f8f9fa', minHeight: 0 }}>
                {selectedCollection && (
                  <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(handleProcessPayment)}>
                      {/* Payment Amount Section */}
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body p-4">
                          <div className="d-flex align-items-center mb-4">
                            <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                              <i className="fa fa-wallet text-primary" style={{ display: 'block' }}></i>
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">Payment Details</h6>
                              <small className="text-muted">Enter the payment amount and date</small>
                            </div>
                          </div>

                          <div className="row g-4">
                            <div className="col-md-6">
                              <label htmlFor="paymentAmount" className="form-label fw-semibold mb-2">
                                Payment Amount <span className="text-danger">*</span>
                              </label>
                              <div className="position-relative">
                                <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: errors.paymentAmount ? '2px solid #dc3545' : '1px solid #dee2e6' }}>
                                  <span className="input-group-text bg-white border-0 px-4">
                                    <i className="fa fa-credit-card text-primary fa-lg" style={{ display: 'block' }}></i>
                                  </span>
                                  <input
                                    type="number"
                                    id="paymentAmount"
                                    className={`form-control border-0 ${errors.paymentAmount ? 'is-invalid' : ''}`}
                                    placeholder="0.00"
                                    style={{ fontSize: '1.1rem', fontWeight: 500 }}
                                    {...register('paymentAmount')}
                                    min="0.01"
                                    step="0.01"
                                  />
                                </div>
                                {errors.paymentAmount && (
                                  <div className="text-danger small mt-2">
                                    <i className="fa fa-exclamation-circle me-1"></i>
                                    {errors.paymentAmount.message}
                                  </div>
                                )}
                                {loanAccount?.totalOutstanding && (
                                  <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff' }}>
                                    <small className="text-primary d-flex align-items-center">
                                      <i className="fa fa-info-circle me-2"></i>
                                      <span>Outstanding balance: <strong>{formatCurrency(loanAccount.totalOutstanding)}</strong></span>
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-md-6">
                              <label htmlFor="valueDate" className="form-label fw-semibold mb-2">
                                Payment Date <span className="text-danger">*</span>
                              </label>
                              <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: errors.valueDate ? '2px solid #dc3545' : '1px solid #dee2e6' }}>
                                <span className="input-group-text bg-white border-0 px-4">
                                  <i className="fa fa-calendar text-primary fa-lg" style={{ display: 'block' }}></i>
                                </span>
                                <input
                                  type="date"
                                  id="valueDate"
                                  className={`form-control border-0 ${errors.valueDate ? 'is-invalid' : ''}`}
                                  style={{ fontSize: '1.1rem', fontWeight: 500 }}
                                  {...register('valueDate')}
                                />
                              </div>
                              {errors.valueDate && (
                                <div className="text-danger small mt-2">
                                  <i className="fa fa-exclamation-circle me-1"></i>
                                  {errors.valueDate.message}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Additional Information Section */}
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-body p-4">
                          <div className="d-flex align-items-center mb-4">
                            <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(108,117,125,0.2)' }}>
                              <i className="fa fa-file-text text-secondary" style={{ display: 'block' }}></i>
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">Additional Information</h6>
                              <small className="text-muted">Optional details for tracking</small>
                            </div>
                          </div>

                          <div className="row g-4">
                            <div className="col-md-6">
                              <label htmlFor="externalRef" className="form-label fw-semibold mb-2">
                                External Reference
                              </label>
                              <div className="input-group" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                <span className="input-group-text bg-white border-0 px-4">
                                  <i className="fa fa-tag text-secondary" style={{ display: 'block' }}></i>
                                </span>
                                <input
                                  type="text"
                                  id="externalRef"
                                  className="form-control border-0"
                                  placeholder="e.g., Transaction ID or Reference"
                                  {...register('externalRef')}
                                />
                              </div>
                              <small className="text-muted d-block mt-2">
                                <i className="fa fa-lightbulb-o me-1"></i>
                                Reference from external payment system (optional)
                              </small>
                            </div>

                            <div className="col-md-6">
                              <label htmlFor="remarks" className="form-label fw-semibold mb-2">
                                Remarks
                              </label>
                              <div className="input-group" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                <span className="input-group-text bg-white border-0 px-4">
                                  <i className="fa fa-comment text-secondary" style={{ display: 'block' }}></i>
                                </span>
                                <input
                                  type="text"
                                  id="remarks"
                                  className="form-control border-0"
                                  placeholder="Add any notes or comments"
                                  {...register('remarks')}
                                />
                              </div>
                              <small className="text-muted d-block mt-2">
                                <i className="fa fa-lightbulb-o me-1"></i>
                                Additional notes about this payment (optional)
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top position-relative">
                        {(isProcessingPayment || recordPaymentMutation.isPending) && (
                          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10, borderRadius: '0.5rem' }}>
                            <div className="text-center">
                              <div className="spinner-border text-success mb-2" role="status" style={{ width: '3rem', height: '3rem' }}>
                                <span className="visually-hidden">Processing...</span>
                              </div>
                              <div className="fw-semibold text-dark">Processing Payment...</div>
                              <small className="text-muted d-block mt-1">Please wait while we process your payment</small>
                            </div>
                          </div>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-secondary px-5 py-2 rounded-pill"
                          data-bs-dismiss="modal"
                          disabled={isProcessingPayment || recordPaymentMutation.isPending}
                        >
                          <i className="fa fa-times me-2"></i>
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-success px-5 py-2 rounded-pill shadow-sm fw-semibold"
                          disabled={isProcessingPayment || recordPaymentMutation.isPending}
                          style={{ minWidth: '180px' }}
                        >
                          {(isProcessingPayment || recordPaymentMutation.isPending) ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Processing Payment...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-check-circle me-2"></i>
                              Process Payment
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </FormProvider>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Early Repayment Modal */}
        <div className="modal fade modal-slide-in-left" id="DueCollectionEarlyRepayment" tabIndex={-1} aria-labelledby="DueCollectionEarlyRepaymentLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-vertical" style={{ maxWidth: '900px', width: '90%' }}>
            {/* The panel is fixed at full viewport height, so the content scrolls to keep the Pay button reachable */}
            <div className="modal-content rounded-0 border-0 shadow-lg" style={{ overflowY: 'auto' }}>
              {!earlyRepaymentCalculation ? (
                <div style={{ backgroundColor: '#f8f9fa' }}>
                  {/* Header */}
                  <div className="border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                    <div className="w-100">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.3)' }}>
                            <i className="fa fa-handshake-o fa-2x text-warning" style={{ display: 'block' }}></i>
                          </div>
                          <div>
                            <h4 className="modal-title mb-0 text-dark fw-bold" id="DueCollectionEarlyRepaymentLabel">
                              Process Early Repayment
                            </h4>
                            <p className="text-muted mb-0 mt-1 small">Calculate and process early loan repayment</p>
                          </div>
                        </div>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                      </div>

                      {/* Quick Info Cards */}
                      <div className="row g-3">
                        <div className="col-6">
                          <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                <i className="fa fa-user text-primary" style={{ display: 'block' }}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small">Customer</div>
                                <div className="text-dark fw-semibold small">{selectedCollection?.customerName || loanAccount?.customerNumber || 'N/A'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(255,193,7,0.2)' }}>
                                <i className="fa fa-money text-warning" style={{ display: 'block' }}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small">Outstanding</div>
                                <div className="text-danger fw-bold">{formatCurrency(loanAccount?.totalOutstanding || selectedCollection?.dueAmount || 0)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Body - Calculate State */}
                  <div className="p-5 text-center">
                    <div className="mb-4">
                      <div className="rounded-circle p-4 mx-auto mb-4 d-inline-block" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.3)' }}>
                        <i className="fa fa-calculator fa-3x text-warning" style={{ display: 'block' }}></i>
                      </div>
                      <h4 className="mb-3 fw-bold">Calculate Early Repayment Amount</h4>
                      <p className="text-muted mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
                        Click the button below to calculate the total amount required for early repayment, including outstanding principal, interest, and any applicable penalties.
                      </p>
                    </div>
                    <button
                      className="btn btn-warning btn-lg px-5 py-3 rounded-pill shadow-sm fw-semibold"
                      onClick={calculateEarlyRepaymentAmount}
                      disabled={isCalculatingEarlyRepayment || getEarlyRepaymentQuoteMutation.isPending}
                    >
                      {isCalculatingEarlyRepayment || getEarlyRepaymentQuoteMutation.isPending ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Calculating...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-calculator me-2"></i>
                          Calculate Early Repayment Amount
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ backgroundColor: '#f8f9fa' }}>
                  {/* Header */}
                  <div className="border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                    <div className="w-100">
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.3)' }}>
                            <i className="fa fa-handshake-o fa-2x text-warning" style={{ display: 'block' }}></i>
                          </div>
                          <div>
                            <h4 className="modal-title mb-0 text-dark fw-bold" id="DueCollectionEarlyRepaymentLabel">
                              Process Early Repayment
                            </h4>
                            <p className="text-muted mb-0 mt-1 small">Review and confirm early repayment details</p>
                          </div>
                        </div>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                      </div>

                      {/* Quick Info Cards */}
                      <div className="row g-3">
                        <div className="col-6">
                          <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                <i className="fa fa-user text-primary" style={{ display: 'block' }}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small">Customer</div>
                                <div className="text-dark fw-semibold small">{selectedCollection?.customerName || loanAccount?.customerNumber || 'N/A'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.2)' }}>
                                <i className="fa fa-money text-warning" style={{ display: 'block' }}></i>
                              </div>
                              <div className="flex-grow-1">
                                <div className="text-muted small">Total Payoff</div>
                                <div className="text-warning fw-bold">{formatCurrency(earlyRepaymentCalculation.totalPayoffAmount || 0)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-4">
                    <FormProvider {...earlyRepaymentMethods}>
                      <form onSubmit={handleSubmitEarlyRepayment(handleProcessEarlyRepayment)}>
                        {/* Breakdown Section */}
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.3)' }}>
                                <i className="fa fa-calculator text-warning" style={{ display: 'block' }}></i>
                              </div>
                              <div>
                                <h6 className="mb-0 fw-bold">Payment Breakdown</h6>
                                <small className="text-muted">Detailed breakdown of the total payoff amount</small>
                              </div>
                            </div>

                            <div className="row g-4">
                              <div className="col-md-4">
                                <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#e7f3ff', border: '1px solid rgba(0,123,255,0.2)' }}>
                                      <i className="fa fa-money text-primary" style={{ display: 'block' }}></i>
                                    </div>
                                    <div className="text-muted small">Outstanding Principal</div>
                                  </div>
                                  <div className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>
                                    {formatCurrency(earlyRepaymentCalculation.principalOutstanding || 0)}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#d4edda', border: '1px solid rgba(40,167,69,0.2)' }}>
                                      <i className="fa fa-percentage text-success" style={{ display: 'block' }}></i>
                                    </div>
                                    <div className="text-muted small">Accrued Interest</div>
                                  </div>
                                  <div className="fw-bold text-success" style={{ fontSize: '1.1rem' }}>
                                    {formatCurrency(earlyRepaymentCalculation.accruedInterest || 0)}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                  <div className="d-flex align-items-center mb-2">
                                    <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.2)' }}>
                                      <i className="fa fa-exclamation-circle text-danger" style={{ display: 'block' }}></i>
                                    </div>
                                    <div className="text-muted small">Unpaid Penalties</div>
                                  </div>
                                  <div className="fw-bold text-danger" style={{ fontSize: '1.1rem' }}>
                                    {formatCurrency(earlyRepaymentCalculation.unpaidPenalties || 0)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Payment Details Section */}
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.3)' }}>
                                <i className="fa fa-calendar text-warning" style={{ display: 'block' }}></i>
                              </div>
                              <div>
                                <h6 className="mb-0 fw-bold">Payment Details</h6>
                                <small className="text-muted">Enter the payment date</small>
                              </div>
                            </div>

                            <div className="row g-4">
                              <div className="col-md-6">
                                <label htmlFor="earlyRepaymentValueDate" className="form-label fw-semibold mb-2">
                                  Payment Date <span className="text-danger">*</span>
                                </label>
                                <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: earlyRepaymentErrors.valueDate ? '2px solid #dc3545' : '1px solid #dee2e6' }}>
                                  <span className="input-group-text bg-white border-0 px-4">
                                    <i className="fa fa-calendar text-warning fa-lg" style={{ display: 'block' }}></i>
                                  </span>
                                  <input
                                    type="date"
                                    id="earlyRepaymentValueDate"
                                    className={`form-control border-0 ${earlyRepaymentErrors.valueDate ? 'is-invalid' : ''}`}
                                    style={{ fontSize: '1.1rem', fontWeight: 500 }}
                                    {...registerEarlyRepayment('valueDate')}
                                  />
                                </div>
                                {earlyRepaymentErrors.valueDate && (
                                  <div className="text-danger small mt-2">
                                    <i className="fa fa-exclamation-circle me-1"></i>
                                    {earlyRepaymentErrors.valueDate.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Additional Information Section */}
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(108,117,125,0.2)' }}>
                                <i className="fa fa-file-text text-secondary" style={{ display: 'block' }}></i>
                              </div>
                              <div>
                                <h6 className="mb-0 fw-bold">Additional Information</h6>
                                <small className="text-muted">Optional details for tracking</small>
                              </div>
                            </div>

                            <div className="row g-4">
                              <div className="col-md-6">
                                <label htmlFor="earlyRepaymentExternalRef" className="form-label fw-semibold mb-2">
                                  External Reference
                                </label>
                                <div className="input-group" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                  <span className="input-group-text bg-white border-0 px-4">
                                    <i className="fa fa-tag text-secondary" style={{ display: 'block' }}></i>
                                  </span>
                                  <input
                                    type="text"
                                    id="earlyRepaymentExternalRef"
                                    className="form-control border-0"
                                    placeholder="e.g., Transaction ID or Reference"
                                    {...registerEarlyRepayment('externalRef')}
                                  />
                                </div>
                                <small className="text-muted d-block mt-2">
                                  <i className="fa fa-lightbulb-o me-1"></i>
                                  Reference from external payment system (optional)
                                </small>
                              </div>

                              <div className="col-md-6">
                                <label htmlFor="earlyRepaymentRemarks" className="form-label fw-semibold mb-2">
                                  Remarks
                                </label>
                                <div className="input-group" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                  <span className="input-group-text bg-white border-0 px-4">
                                    <i className="fa fa-comment text-secondary" style={{ display: 'block' }}></i>
                                  </span>
                                  <input
                                    type="text"
                                    id="earlyRepaymentRemarks"
                                    className="form-control border-0"
                                    placeholder="Add any notes or comments"
                                    {...registerEarlyRepayment('remarks')}
                                  />
                                </div>
                                <small className="text-muted d-block mt-2">
                                  <i className="fa fa-lightbulb-o me-1"></i>
                                  Additional notes about this payment (optional)
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top position-relative">
                          {(isProcessingEarlyRepayment || recordEarlyRepaymentMutation.isPending) && (
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10, borderRadius: '0.5rem' }}>
                              <div className="text-center">
                                <div className="spinner-border text-warning mb-2" role="status" style={{ width: '3rem', height: '3rem' }}>
                                  <span className="visually-hidden">Processing...</span>
                                </div>
                                <div className="fw-semibold text-dark">Processing Early Repayment...</div>
                                <small className="text-muted d-block mt-1">Please wait while we process your early repayment</small>
                              </div>
                            </div>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline-secondary px-5 py-2 rounded-pill"
                            data-bs-dismiss="modal"
                            disabled={isProcessingEarlyRepayment || recordEarlyRepaymentMutation.isPending}
                          >
                            <i className="fa fa-times me-2"></i>
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-warning px-5 py-2 rounded-pill shadow-sm fw-semibold"
                            disabled={isProcessingEarlyRepayment || recordEarlyRepaymentMutation.isPending}
                            style={{ minWidth: '200px' }}
                          >
                            {(isProcessingEarlyRepayment || recordEarlyRepaymentMutation.isPending) ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-check-circle me-2"></i>
                                Process Early Repayment
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </FormProvider>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <SendReminderModal
          target={reminderTarget}
          onHide={() => setReminderTarget(null)}
          onSent={(sent) => {
            if (reminderTarget?.accountId) {
              setLastNotes(prev => ({ ...prev, [reminderTarget.accountId]: describeReminder(sent) }));
            }
          }}
        />

        {/* Add Collection Note Modal */}
        <div className="modal fade modal-slide-in-left" id="DueCollectionNote" tabIndex={-1} aria-labelledby="DueCollectionNoteLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-vertical" style={{ maxWidth: '700px', width: '85%' }}>
            <div className="modal-content rounded-0 border-0 shadow-lg">
              <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="w-100">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#d4edda', border: '1px solid rgba(40,167,69,0.3)' }}>
                        <i className="fa fa-sticky-note fa-2x text-success" style={{ display: 'block' }}></i>
                      </div>
                      <div>
                        <h4 className="modal-title mb-0 text-dark fw-bold" id="DueCollectionNoteLabel">Add Collection Note</h4>
                        <p className="text-muted mb-0 mt-1 small">Add a note for this loan account</p>
                      </div>
                    </div>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>

                  {/* Account Info */}
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                            <i className="fa fa-user text-primary" style={{ display: 'block' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="text-muted small">Customer</div>
                            <div className="text-dark fw-semibold small">{selectedCollection?.customerName || 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(40,167,69,0.2)' }}>
                            <i className="fa fa-id-card text-success" style={{ display: 'block' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="text-muted small">Account</div>
                            <div className="text-dark fw-semibold small">{selectedCollection?.accountNumber || 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="mb-3">
                      <label className="form-label fw-semibold mb-2">Context</label>
                      <select
                        className="form-select"
                        value={noteContext}
                        onChange={(e) => setNoteContext(e.target.value as CollectionNoteContext)}
                        disabled={isSubmittingNote}
                      >
                        <option value="GENERAL">General</option>
                        <option value="ARRANGEMENTS">Arrangements</option>
                        <option value="COLLECTION">Collection</option>
                        <option value="APPLICATION_NOTES">Application Notes</option>
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold mb-2">Note</label>
                      <textarea
                        className="form-control"
                        rows={4}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Enter your collection note here..."
                        disabled={isSubmittingNote}
                        maxLength={2000}
                      />
                      <small className="text-muted">{noteText.length}/2000 characters</small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 px-4 pb-4" style={{ backgroundColor: '#f8f9fa' }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 py-2 rounded-pill"
                  data-bs-dismiss="modal"
                  disabled={isSubmittingNote}
                >
                  <i className="fa fa-times me-2"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success px-4 py-2 rounded-pill shadow-sm fw-semibold"
                  onClick={handleSubmitNote}
                  disabled={isSubmittingNote || !noteText.trim()}
                >
                  {isSubmittingNote ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Adding...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-check-circle me-2"></i>
                      Add Remarks
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* View All Notes Modal */}
        {showViewNotesModal && viewNotesCollection && (
          <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#d4edda', border: '1px solid rgba(40,167,69,0.3)' }}>
                      <i className="fa fa-sticky-note fa-lg text-success" style={{ display: 'block' }}></i>
                    </div>
                    <div>
                      <h5 className="modal-title mb-0 fw-bold">Collection Remarks</h5>
                      <p className="text-muted mb-0 small">
                        {viewNotesCollection.customerName || 'N/A'} — {viewNotesCollection.accountNumber || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowViewNotesModal(false)}
                  ></button>
                </div>
                <div className="modal-body p-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {isLoadingAllNotes ? (
                    <div className="text-center py-5">
                      <span className="spinner-border text-success" role="status"></span>
                      <p className="text-muted mt-3">Loading remarks...</p>
                    </div>
                  ) : allNotes.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fa fa-sticky-note fa-3x text-muted mb-3" style={{ display: 'block' }}></i>
                      <p className="text-muted">No remarks found for this account.</p>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {allNotes.map((note) => (
                        <div key={note.id} className="card border shadow-sm" style={{ borderRadius: '8px' }}>
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <span className={`badge px-2 py-1 ${
                                note.context === 'COLLECTION' ? 'bg-danger-subtle text-danger-emphasis' :
                                note.context === 'ARRANGEMENTS' ? 'bg-info-subtle text-info-emphasis' :
                                note.context === 'APPLICATION_NOTES' ? 'bg-warning-subtle text-warning-emphasis' :
                                'bg-secondary-subtle text-secondary-emphasis'
                              }`}>
                                {note.context || 'GENERAL'}
                              </span>
                              <small className="text-muted">
                                {note.createdDate ? formatDate(note.createdDate) : '—'}
                              </small>
                            </div>
                            <p className="mb-1" style={{ whiteSpace: 'pre-wrap' }}>{note.note}</p>
                            {note.createdBy && (
                              <small className="text-muted">
                                <i className="fa fa-user me-1"></i>{note.createdBy}
                              </small>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer border-0 px-4 pb-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={() => setShowViewNotesModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default DueCollections;